import API_URLS from '@Constants/apiUrls.constants';
import { CommonConstants } from '@Constants/index';
import { GetLists } from '@Models/common.models';
import {
  ScriptsMutationResponse,
  ArchiveScripts,
  DeleteScripts,
  ScriptsListResponse,
  ScriptResponse,
  ScriptAdd,
  ScriptEdit,
} from '@Models/scripts.models';
import query from '@Utils/common/query.utils';

import { baseApi } from './baseApi.service';

export const scriptsApi = baseApi
  .enhanceEndpoints({ addTagTypes: ['Scripts', 'Script'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      /** Fetch scripts list with given queries */
      getScriptsLists: builder.query<ScriptsListResponse, GetLists>({
        query: ({ data: { direction, ...resData }, isArchive = false }) =>
          `${API_URLS.SCRIPTS}${isArchive ? '?archive&' : '?'}${
            direction || CommonConstants.INITIAL_SORT_DIRECTION
          }&${query.stringify({ ...resData })}`,
        providesTags: ['Scripts'],
      }),
      /** Fetch script with given id */
      getScript: builder.query<ScriptResponse, string>({
        query: (id) => `${API_URLS.SCRIPTS}/${id}`,
        providesTags: ['Script'],
      }),
      /** Archive the scripts with the given ids */
      archiveScripts: builder.mutation<ScriptsMutationResponse, ArchiveScripts>(
        {
          query: (body) => ({
            url: `${API_URLS.SCRIPTS}/archive`,
            method: 'PUT',
            body,
          }),
          invalidatesTags: ['Scripts'],
        }
      ),
      /** Delete the script with the given id */
      deleteScripts: builder.mutation<ScriptsMutationResponse, DeleteScripts>({
        query: (body) => ({
          url: `${API_URLS.SCRIPTS}/delete`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: ['Scripts'],
      }),
      /** Add new script */
      addScript: builder.mutation<ScriptResponse, ScriptAdd>({
        query: (body) => ({
          url: `${API_URLS.SCRIPTS}`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['Script', 'Scripts'],
      }),
      addScriptsFromGroup: builder.mutation<
        void,
        { globalScriptsIds: string[]; userId: string | number | null }
      >({
        query: (body) => ({
          url: `${API_URLS.SCRIPTS}/group`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['Scripts'],
      }),
      /** Edit the script with the given id */
      editScript: builder.mutation<ScriptResponse, ScriptEdit>({
        query: (body) => ({
          url: `${API_URLS.SCRIPTS}/${body.id}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: ['Script', 'Scripts'],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetScriptsListsQuery,
  useGetScriptQuery,
  useArchiveScriptsMutation,
  useDeleteScriptsMutation,
  useAddScriptMutation,
  useAddScriptsFromGroupMutation,
  useEditScriptMutation,
} = scriptsApi;
